import React from "react";
import { Button, Badge, OverlayTrigger, Tooltip, Form } from "react-bootstrap";
import {
  FaCheckCircle,
  FaRocket,
  FaBolt,
  FaLock,
  FaArrowUp,
  FaInfoCircle,
} from "react-icons/fa";
import { getPlanPrice } from "../../../utils/static";
import {
  calculatePlanPrice,
  validateApiCallsForPlan,
  getPlanPricingConfig,
  formatPlanDisplayPrice,
  FORWARD_RATES_ADDON,
  YEARLY_DISCOUNT,
} from "../../../utils/planPricing";
import ApiCallsSelector from "../../shared/ApiCallsSelector";
import PlanApiAccessList from "../../shared/PlanApiAccessList";
import { formatPlanApiCallsLabel } from "../../../utils/planApiAccess";

const getForwardRatesAddonLabel = (billingCycle, currency) => {
  const cur = String(currency || "INR").toUpperCase();
  const monthly =
    cur === "USD" ? FORWARD_RATES_ADDON.USD : FORWARD_RATES_ADDON.INR;
  const isYearly = String(billingCycle).toLowerCase() === "yearly";
  const amount = isYearly
    ? monthly * 12 * (1 - YEARLY_DISCOUNT)
    : monthly;
  const symbol = cur === "USD" ? "$" : "₹";
  const suffix = isYearly ? "/year" : "/mo";
  return `Forward Rate API add-on — ${symbol}${formatPlanDisplayPrice(
    amount,
    cur
  )}${suffix}`;
};

const PlanCard = ({
  plan,
  billingCycle,
  currency,
  apiCalls,
  onApiCallsChange,
  onChoosePlan,
  isCurrentPlan,
  isDisabled,
  planStatus,
  disabledReason,
  priceExcludesGst = true,
  wide = false,
  forwardRatesAddon = false,
  onForwardRatesAddonChange,
}) => {
  const IconComponent = plan.icon;

  const getAvailability = () => {
    if (plan.isFree) return { valid: true, apiCalls: plan.apiLimit };
    return validateApiCallsForPlan(plan.backendName, apiCalls);
  };

  const availability = getAvailability();

  const getDisplayPrice = () => {
    if (plan.isFree) return 0;
    if (!availability.valid) return null;
    try {
      const result = calculatePlanPrice(plan.backendName, availability.apiCalls, {
        interval: billingCycle,
        currency,
        forwardRatesAddon,
      });
      if (priceExcludesGst) {
        return currency === "USD" ? result.subtotalUSD : Math.round(result.subtotalINR);
      }
      return currency === "USD" ? result.subtotalUSD : Math.round(result.amount);
    } catch {
      return getPlanPrice(plan, currency, billingCycle);
    }
  };

  const getDisplayApiLimit = () => {
    if (plan.isFree) return plan.apiLimit;
    if (availability.valid) return availability.apiCalls;
    return getPlanPricingConfig(plan.backendName)?.includedCalls ?? plan.apiLimit;
  };

  const getApiCallsLabel = () => {
    if (!plan.isFree && !availability.valid) return null;
    return formatPlanApiCallsLabel(
      plan.backendName,
      plan.isFree ? 100 : availability.apiCalls
    );
  };

  const finalPrice = getDisplayPrice();

  const getButtonText = () => {
    if (isCurrentPlan) return "Current Plan";
    if (isDisabled) return "Not Available";
    if (!plan.isFree && !availability.valid) return "Enter valid API calls";
    if (planStatus === "upgrade") return "Upgrade Now";
    return "Choose Plan";
  };

  const renderButton = () => {
    const disabled =
      isCurrentPlan || isDisabled || (!plan.isFree && !availability.valid);

    const button = (
      <Button
        className="theme-btn1 w-100"
        onClick={() => onChoosePlan(plan)}
        variant="primary"
        size="lg"
        disabled={disabled}
        style={{
          background: disabled
            ? "#6c757d"
            : isCurrentPlan
              ? "linear-gradient(45deg, #28a745, #20c997)"
              : "linear-gradient(45deg, #007bff, #0056b3)",
          border: "none",
          padding: "12px 20px",
          borderRadius: "5px",
          fontWeight: "bold",
        }}
      >
        {isCurrentPlan && <FaCheckCircle className="me-2" />}
        {isDisabled && <FaLock className="me-2" />}
        {planStatus === "upgrade" && !isCurrentPlan && !isDisabled && (
          <FaArrowUp className="me-2" />
        )}
        {!isCurrentPlan && !isDisabled && planStatus !== "upgrade" && (
          <FaRocket className="me-2" />
        )}
        {getButtonText()}
        {!disabled && (
          <span>
            <i className="fa-solid fa-arrow-right ms-2"></i>
          </span>
        )}
      </Button>
    );

    if (isDisabled && disabledReason) {
      return (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>{disabledReason}</Tooltip>}
        >
          <div>{button}</div>
        </OverlayTrigger>
      );
    }

    return button;
  };

  return (
    <div
      className={`single-pricing-box h-100 position-relative ${
        plan.popular ? "active" : ""
      } ${isCurrentPlan ? "border border-success border-2 rounded" : ""} ${
        isDisabled ? "opacity-75" : ""
      }`}
      style={{
        minHeight: wide ? "720px" : "680px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {plan.popular && !isDisabled && (
        <div className="most-popular">
          <p>Most Popular</p>
        </div>
      )}

      {isCurrentPlan && (
        <div className="position-absolute top-0 end-0 m-2" style={{ zIndex: 2 }}>
          <Badge bg="success" className="px-2 py-1 rounded-pill">
            <FaCheckCircle className="me-1" />
            Current
          </Badge>
        </div>
      )}

      {planStatus === "upgrade" && !isCurrentPlan && !isDisabled && (
        <div className="position-absolute top-0 start-0 m-2" style={{ zIndex: 2 }}>
          <Badge bg="info" className="px-2 py-1 rounded-pill">
            <FaArrowUp className="me-1" />
            Upgrade
          </Badge>
        </div>
      )}

      {isDisabled && (
        <div
          className="position-absolute top-0 start-50 translate-middle"
          style={{ zIndex: 2 }}
        >
          <Badge bg="secondary" className="px-3 py-2 rounded-pill">
            <FaLock className="me-1" />
            Downgrade Not Allowed
          </Badge>
        </div>
      )}

      <div
        className="text-center mb-3"
        style={{
          background: isDisabled ? "#6c757d" : plan.gradient,
          color: "white",
          padding: "20px 15px",
          borderRadius: "8px 8px 0 0",
          margin: "-20px -20px 20px -20px",
        }}
      >
        <IconComponent size={28} className="mb-2" />
        <p
          className="title text-white fw-bold mb-1"
          style={{ color: "white !important" }}
        >
          {plan.displayName}
        </p>
        <p className="small mb-0 opacity-90">{plan.subtitle}</p>
      </div>

      <div className="text-center mb-3">
        <h2 className="fw-bold" style={{ color: "#007bff" }}>
          {plan.isFree ? (
            "Free"
          ) : !availability.valid ? (
            <span className="fs-5 text-muted">—</span>
          ) : (
            <>
              {currency === "USD" ? "$" : "₹"}
              {formatPlanDisplayPrice(finalPrice, currency)}
              <span style={{ fontSize: "16px", color: "#6c757d" }}>
                /{billingCycle === "yearly" ? "year" : "month"}
              </span>
            </>
          )}
        </h2>

        {!plan.isFree && availability.valid && currency === "INR" && (
          <div className="small text-muted">
            {priceExcludesGst ? "+ 18% GST" : "incl. 18% GST"}
          </div>
        )}

        {plan.isFree && (
          <div className="mt-2 small fw-bold" style={{ color: "#10b981" }}>
            <FaInfoCircle className="me-2" />
            No card required to activate Free Plan
          </div>
        )}

        {billingCycle === "yearly" && availability.valid && !plan.isFree && (
          <div className="small text-success">
            <FaBolt className="me-1" />
            Save 10% with yearly billing
          </div>
        )}
      </div>

      <div className="text-center mb-3">
        <div className="badge bg-light text-dark border px-3 py-2">
          {plan.unlimited
            ? "🔥 Unlimited API Calls"
            : availability.valid
              ? `🚀 ${getDisplayApiLimit()?.toLocaleString()} API calls/month`
              : "🚀 Enter valid API calls"}
        </div>
      </div>

      <div className="border"></div>

      {!plan.isFree && onApiCallsChange && (
        <ApiCallsSelector
          compact
          billingCycle={billingCycle}
          selectedPlanBackendName={plan.backendName}
          value={apiCalls}
          onChange={onApiCallsChange}
        />
      )}

      {!plan.isFree &&
        (plan.backendName === "PRO" || plan.backendName === "ULTIMATE") &&
        onForwardRatesAddonChange && (
          <Form.Check
            type="checkbox"
            id={`forward-addon-${plan.backendName}`}
            className="mb-3 small"
            label={getForwardRatesAddonLabel(billingCycle, currency)}
            checked={forwardRatesAddon}
            onChange={(e) => onForwardRatesAddonChange(e.target.checked)}
            disabled={isDisabled}
          />
        )}

      <div style={{ flex: "1" }}>
        <PlanApiAccessList
          planBackendName={plan.backendName}
          apiCallsLabel={getApiCallsLabel()}
        />
      </div>

      <div className="space30"></div>
      <div className="button" style={{ marginTop: "auto" }}>
        {renderButton()}
      </div>
    </div>
  );
};

export default PlanCard;
